<template>
  <div :id="ariaDescribedByName" class="mt-8 flex relative items-center" :class="customClasses" tabindex="-1">
    <div class="mr-4 self-start">
      <slot name="customIcon" />
      <warning-outline-icon v-if="showErrorIcon" id="error-icon" :color="errorIconColor" :height="16" :width="16" />
      <tick-circle-outline-icon v-if="showSuccessIcon" id="success-icon" color="#229150" :height="16" :width="16" />
    </div>
    <p :class="['text-field-helper', textfieldHelperClasses]" aria-live="assertive" v-html="text" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import helperStatus from '../../helpers/status';
import WarningOutlineIcon from '../../graphics/icons/WarningOutlineIcon.vue';
import TickCircleOutlineIcon from '../../graphics/icons/TickCircleOutlineIcon.vue';

export default Vue.extend({
  name: 'HelperText',
  components: {
    WarningOutlineIcon,
    TickCircleOutlineIcon,
  },
  props: {
    id: {
      type: String || null,
      required: false,
      default: null,
    },
    text: {
      type: String,
      required: true,
      default: '',
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    customClasses: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    showSuccessIcon(): Boolean {
      return this.status === helperStatus.Valid && !this.$slots.customIcon;
    },
    showErrorIcon(): Boolean {
      return (this.status === helperStatus.Error || this.status === helperStatus.Warn) && !this.$slots.customIcon;
    },
    errorIconColor(): String {
      return this.status === helperStatus.Error ? '#CE1B04' : '#644900';
    },
    ariaDescribedByName(): String | null {
      return this.id ? `${this.id}-helper` : null;
    },
    textfieldHelperClasses(): String {
      if (this.status === helperStatus.Error) {
        return 'text-field-error';
      }
      if (this.status === helperStatus.Warn) {
        return 'text-field-warn';
      }
      if (this.status === helperStatus.Valid) {
        return 'text-field-valid';
      }
      return '';
    },
  },
});
</script>

<style lang="scss" scoped>
.text-field-helper {
  color: theme('colors.grey.text.subdued');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.xs');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.4px;
}
.text-field-helper.text-field-error {
  color: theme('colors.critical.contrast');
}
.text-field-helper.text-field-warn {
  color: theme('colors.warning.contrast');
}
.text-field-helper.text-field-valid {
  color: theme('colors.success.contrast');
}
</style>
